import Backbone from 'backbone'
import $ from 'jquery'
import _ from 'lodash'
import colorIcon from 'scoreboard/components/color-icon'
import i18n from 'scoreboard/components/i18n'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'
import nonAbbreviatedValuesTooltip from 'scoreboard/dashboards/widgets/chart/non-abbreviated-values-tooltip.hbs'
import unwrapModel from 'scoreboard/components/unwrap-model'
import { enabled } from './serie-drilldown'
import prepareTableData from './chart-data/prepare-table-data'
import template from './chart-data-table.hbs'
import labelCellTemplate from './chart-data-table-label-cell.hbs'
import serieDrilldown from './serie-drilldown'

const ChartDataTable = Backbone.View.extend({
	className: 'chart-table table colored-cells',
	tagName: 'table',

	events: {
		'click td.object': 'onClickSerie'
	},

	/**
	 * @type ChartDesigner
	 */
	model: null,

	/**
	 * @param {Object} options
	 *
	 * @param {Boolean} options.showSeriesLabel true if the label for each serie
	 * should be shown
	 *
	 * @param {Boolean} options.showSeriesColor true if the colored dot for each
	 * serie should be shown
	 *
	 * @param {Boolean} options.showData true if the data points should be shown
	 *
	 * @param {ChartTableModel} [options.widgetModel] if this table is in a
	 * dashboard widget, this is the widget's Model
	 */
	initialize: function (options) {
		this.options = options
	},

	render: function() {
		let plotType = this.model.get('plotType')
		this.$el.empty()
			.append(template())
			.addClass(plotType)

		let { preparedData, serieIconColors } = this.prepareTableData()

		if (plotType === 'tree-map') {
			this.renderTreeMapHeader(preparedData)
			this.renderTreeMapBody(preparedData)
		} else {
			this.renderStandardHeader(preparedData)
			this.renderStandardBody(preparedData, serieIconColors)
		}

		// #42433 if there aren't any data columns (ie only the label/color column),
		// hide the header row
		if (this.$('th.series-data').length === 0) {
			this.$el.addClass('has-no-thead')
		}

		this.renderNonAbbreviatedTooltip()

		return this
	},

	prepareTableData: function () {
		let prepared = prepareTableData({
			chart: this.model,
			chartData: this.model.data,
			forChart: false
		})

		this.regressionSeriesByDataSerieId = prepared.regressionSeriesByDataSerieId

		return prepared
	},

	renderStandardHeader: function (preparedData) {
		let headerEl = this.$('thead')
		let plots = preparedData.plots
		let hasMultiplePlots = plots.length > 1

		if (hasMultiplePlots) {
			this.renderMultiplePlotHeaderRow({ plots, headerEl })
		}

		let headerRow = this.addHeaderRow(headerEl)

		// If there are multiple plots (ie two breakdowns), there is a
		// two-row header. In that case we already rendered a two rowspan cell
		// to cover the label column, so we don't need to render a td here
		if (!hasMultiplePlots && this.showLabelColumn()) {
			this.renderLabelColumnHeader(headerRow)
		}

		if (this.options.showData) {
			this.renderSerieDataColumnHeaders({ headerRow, plots })
		}
	},

	renderMultiplePlotHeaderRow: function ({ plots, headerEl }) {
		let headerRow = this.addHeaderRow(headerEl)

		if (this.showLabelColumn()) {
			this.renderLabelColumnHeader(headerRow)
				.attr('rowspan', 2)
		}

		plots.forEach(plot => {
			let colspan = plot.breakdown.range.length
			$('<th>').appendTo(headerRow)
				.attr('colspan', colspan)
				.text(plot.breakdown.title)
		})
	},

	renderLabelColumnHeader: function (headerRow) {
		return $('<th>').appendTo(headerRow)
			// #28360 this column can have a lot of different things in it,
			// so just give it a generic "Item" label
			.text(i18n.get('common.item'))
	},

	renderSerieDataColumnHeaders: function ({ headerRow, plots }) {
		plots.forEach(plot => {
			if (plot.breakdown.range.length === 0) {
				headerRow.append('<th>')
			} else {
				this.rangesToUse(plot).forEach(range => {
					let cell = $('<th>').appendTo(headerRow)
						.addClass('series-data')

					$('<div>').appendTo(cell)
						.addClass('title')
						.text(range.title)

					if (range.subtitle) {
						$('<div>').appendTo(cell)
							.addClass('subtitle')
							.text(range.subtitle)
					}
				})
			}

		})
	},

	renderStandardBody: function (preparedData, serieIconColors) {
		let body = this.$('tbody')
		let plots = preparedData.plots

		if (!plots.length) {
			return
		}

		let serieCount = plots[0].series.length

		_.times(serieCount).forEach(serieIndex => {
			const rowSerie = plots[0].series[serieIndex]
			let underlyingSerie = rowSerie.underlyingSerie

			let row = $('<tr>').appendTo(body)
				.data('underlyingSerie', underlyingSerie)

			if (this.showLabelColumn()) {
				this.renderSerieLabelCell({ row, plots, underlyingSerie, serieIndex, serieIconColors })
			}

			if (this.options.showData) {
				this.renderSerieDataCells({ row, plots, serieIndex })
				this.fillTrendLineData({row, rowSerie})
			}
		})
	},

	renderSerieLabelCell: function ({ row, plots, underlyingSerie, serieIndex, serieIconColors }) {
		let labelCell = $(labelCellTemplate()).appendTo(row)
		let sampleDataSerie = _.get(plots, `[0].series[${serieIndex}]`)

		if (this.options.showSeriesLabel) {
			labelCell.find('.object-info-label').text(sampleDataSerie.title)
		}

		let showColor = this.options.showSeriesColor && sampleDataSerie

		if (showColor) {
			let iconDestination = this.options.showSeriesLabel
				? labelCell.find('.object-info-label')
				: labelCell.find('.object-info')

			iconDestination.prepend(colorIcon(serieIconColors[serieIndex]))
		}

		labelCell.find('.object-info').toggleClass('no-color-icon', !showColor)

		labelCell.toggleClass('has-drilldown',
			enabled({ serie: underlyingSerie }))
	},

	renderSerieDataCells: function ({ row, plots, serieIndex }) {
		plots.forEach(plot => {
			let serie = plot.series[serieIndex]

			if (_.isEmpty(plot.breakdown.range)) {
				// Zero breakdown charts will not have an empty array for
				// breakdown.range, but we know there will be a single
				// value in serie.points
				this.renderPoint({
					serie,
					row,
					point: serie.points[0]
				})
			} else {
				this.rangesToUse(plot).forEach(range => {
					// The range's point could be in series.points OR in
					// series.points.nonAggregatedPoints (if it's a pie with
					// multiple periods aggregated)
					let point = _.flatMap(serie.points, p => p.nonAggregatedPoints)
						.concat(serie.points)
						.filter(s => !!s)
						.find(p => p.rangeId === range.id)

					this.renderPoint({
						serie,
						row,
						point,
						title: range.title,
						subtitle: range.subtitle
					})
				})
			}
		})
	},

	renderPoint: function ({ serie, row, point, title, subtitle }) {
		let colorClasses = this.cellPeformanceColorClasses({ point })

		let cell = $('<td>')
			.addClass('series-data')
			.addClass(colorClasses)
			.appendTo(row)

		let valueWrapper = this.createPointWrapperEl(cell, colorClasses)

		if (point) {
			// #42576 Applicable will only be provided if this is a scorecard
			// backed serie. For other types, always render the data
			if (_.isNil(point.applicable) || point.applicable == true) {
				this.renderApplicablePoint({serie, raw: point.raw, valueWrapper, title, subtitle})
			} else {
				valueWrapper.text(i18n.get('scorecards.node.metric.notapplicable'))
			}
		}
	},

	createPointWrapperEl: function (cell, colorClasses) {
		let colorWrapper = $('<div>').appendTo(cell)
		if (colorClasses.length > 0) {
			colorWrapper.addClass('color-wrapper')
		}

		let valueWrapper = $('<span>').appendTo(colorWrapper)
			.addClass('value')
		return valueWrapper
	},

	renderApplicablePoint: function ({serie, raw, valueWrapper, title, subtitle, forceTooltip}) {
		let formatted = serie.formatter(raw)
		let unshortened = serie.shortenedAndUnshortenedFormatter(raw).unshortened
		const requiresTooltip = unshortened && (formatted != unshortened)

		valueWrapper.text(formatted)
		if (requiresTooltip|| forceTooltip) {
			valueWrapper.attr('data-value', unshortened)

			let tooltipHeader = title
			if (subtitle) {
				tooltipHeader += ' ' + subtitle
			}

			valueWrapper.attr('data-header', tooltipHeader)
		}
	},

	cellPeformanceColorClasses: function ({ point }) {
		// If we have a point for this period and it either has a raw value
		// or is N/A, we can use the performance color on the point.
		if (point && point.performanceColor && (_.isNumber(point.raw) || !point.applicable)) {
			return [ 'color', point.performanceColor ]
		}

		return []
	},

	fillTrendLineData: function ({row, rowSerie}) {
		const { underlyingSerie } = rowSerie
		const { id, tempId } = unwrapModel(underlyingSerie)
		const trendLine = this.regressionSeriesByDataSerieId[`${id}`] ||
			this.regressionSeriesByDataSerieId[tempId]

		if (_.isNil(trendLine)) {
			return
		}

		// start at the end of the row and look backwards until we find a raw value
		let offset = -1
		const headerCells = row.parents('table').find('th.series-data')
		const reversedCells = row.find(`td.series-data`).get().reverse()
		_.each(reversedCells, td => {
			const cellText = $(td).find(`span.value`).text()
			if (!_.isEmpty(cellText)) {
				return false
			}
			offset++
		})
		// And now insert the projected values from left to right
		while (offset >= 0) {
			const cell = $(reversedCells[offset]).empty()
			const colorClasses = ['color', 'is-projected-value']
			cell.addClass(colorClasses)
			const colIndex = (offset * -1) - 1

			// construct the tooltip header from the column title
			const headerCell = $(_.nth(headerCells, colIndex))
			const title = headerCell.find('.title').text()
			const subtitle = headerCell.find('.subtitle').text()
			const tooltipTitle = subtitle ? `${title} ${subtitle}` : title

			const prediction = offset === 0
				? _.last(trendLine.predictedValues)
				: _.nth(trendLine.predictedValues, colIndex)
			const valueWrapper = this.createPointWrapperEl(cell, colorClasses)
			this.renderApplicablePoint({
				serie: rowSerie,
				raw: prediction,
				valueWrapper,
				title: `${i18n.get('charts.chart.dataTable.projected')}: ${tooltipTitle}`,
				forceTooltip: true
			})
			offset--
		}
	},

	renderTreeMapHeader: function (preparedData) {
		let headerRow = this.addHeaderRow(this.$('thead'))

		if (this.showLabelColumn()) {
			this.renderLabelColumnHeader(headerRow)
		}

		if (this.options.showData) {
			// Similar to zero-breakdown charts, leave the data column header
			// blank (see #38920)
			headerRow.append('<th>')
		}
	},

	addHeaderRow: function (thead) {
		return $('<tr>').appendTo(thead)
			.addClass('header-row')
	},

	renderTreeMapBody: function (preparedData) {
		let body = this.$('tbody')
		let plot = preparedData.plots[0]
		let serie = plot.series[0]

		// Most chart tables render a row for each serie and a data column for
		// each range in the chart. However, tree maps are always a single serie
		// and have a range for each tree item, and represent hierarchical data.
		// Instead, we'll model the table after Datasets > Explore, with a column
		// for the tree item label and a column for its data, with each tree item
		// in its own row and indented appropriately to its hierarchical level.
		_.forEach(plot.breakdown.range, range => {
			let row = $('<tr>').appendTo(body)

			if (this.showLabelColumn()) {
				$('<td>').appendTo(row)
					.addClass('tree-item-label')
					.toggleClass('indent', range.tableLevel > 0)
					.attr('data-level', range.tableLevel)
					.text(range.title)
			}

			if (this.options.showData) {
				let point = _.find(serie.points, p => p.rangeId == range.id)
				this.renderPoint({
					serie,
					row,
					point,
					title: range.title,
					subtitle: range.subtitle
				})
			}
		})
	},

	renderNonAbbreviatedTooltip: function () {
		this.removeNonAbbreviatedTooltip()

		this.nonAbbreviatedTooltip = new TooltipAndDetails({
			el: this.el,
			// Only popup on cells that have a data-value attribute
			// (ie. abbreviated values and trend projections)
			selector: '.series-data [data-value]',
			tooltipContent: (e) => {
				return nonAbbreviatedValuesTooltip({
					header: e.target.dataset.header,
					value: e.target.dataset.value
				})
			},
			tooltipOptions: {
				extraClass: 'tooltip-and-details non-abbreviated-tooltip datasets-records-non-abbreviated-tooltip'
			}
		})
	},

	removeNonAbbreviatedTooltip: function () {
		this.nonAbbreviatedTooltip && this.nonAbbreviatedTooltip.remove()
		delete this.nonAbbreviatedTooltip
	},

	showLabelColumn: function () {
		if (this.model.get('plotType') === 'tree-map') {
			return this.options.showSeriesLabel
		} else {
			return this.options.showSeriesColor || this.options.showSeriesLabel
		}
	},

	rangesToUse: function (plot) {
		return _.flatMap(plot.breakdown.range, range => {
			// For pies, each plot.breakdown.range may be several periods aggregated
			// together. That's fine for the chart itself, but in the data table, we
			// want the actual underlying data for each period (nonAggregatedDataRanges)
			return range.nonAggregatedDataRanges || range
		})
	},

	onClickSerie: function (e) {
		let underlyingSerie = $(e.currentTarget).closest('tr')
			.data('underlyingSerie')

		serieDrilldown({
			chartDesigner: this.model,
			serie: underlyingSerie,
			widgetModel: this.options.widgetModel
		})
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		this.removeNonAbbreviatedTooltip()
	}

})

export default ChartDataTable

ChartDataTable.__filename__ = 'charts/chart-data-table.js'
